import { Link } from "react-router-dom";
import { ArrowRight, Compass, BookOpen, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Typewriter } from "@/components/ui/typewriter";
import heroImage from "@/assets/images/friends-mountain.webp";

const HeroSection = () => {
  return (
    <section className="relative w-full overflow-hidden bg-background">
      <div className="container py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Text Content */}
          <div className="order-2 lg:order-1">
            {/* Tagline */}
            <div className="inline-flex items-center gap-2 bg-accent/15 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Compass className="w-4 h-4 text-accent" />
              Din guide til sabbatåret
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] mb-2">
              Sabbatår Guide for Unge
            </h1>
            <div className="h-[6.5rem] md:h-[5.5rem] lg:h-[7.5rem] mb-6 overflow-hidden">
              <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-accent">
                <Typewriter
                  text={["– Rejse, Job & Højskole", "– Højskole & Frivilligt", "– Working Holiday", "– Økonomi & Planlægning", "– Backpacking & Eventyr"]}
                  speed={50}
                  deleteSpeed={30}
                  waitTime={2500}
                  loop={true}
                  showCursor={true}
                  cursorChar="|"
                />
              </span>
            </div>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed mb-8">
              Find inspiration, guides og værktøjer til at planlægge det perfekte sabbatår – uanset om du drømmer om at rejse, arbejde eller studere.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-10">
              <Button 
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full px-8 gap-2 shadow-lg shadow-accent/25"
              >
                <Link to="/kategori/sabbataar-intro">
                  Start din rejse
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="border-border text-foreground hover:bg-muted font-semibold rounded-full px-8"
              >
                <Link to="/kategori/ideer-inspiration">
                  Find inspiration
                </Link>
              </Button>
            </div>

            {/* USP badges */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium">40+ artikler</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Compass className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium">9 kategorier</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium">Gratis guides</span>
              </div>
            </div>
          </div>

          {/* Image Collage */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative aspect-[4/3] lg:aspect-square">
              {/* Main image */}
              <div className="absolute inset-4 lg:inset-0 rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={heroImage}
                  alt="Venner på eventyr i bjergene med udsigt over landskabet"
                  className="w-full h-full object-cover"
                  fetchPriority="high"
                />
                {/* App coming soon overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Link 
                    to="/waitlist" 
                    className="text-center px-6 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300 group"
                  >
                    <p className="text-white/90 text-xs font-medium tracking-widest uppercase mb-2">
                      Kommer snart
                    </p>
                    <p className="text-white text-lg md:text-2xl font-bold drop-shadow-lg group-hover:scale-105 transition-transform">
                      Download MySabbat
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      App Store & Google Play
                    </p>
                  </Link>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
              
              {/* Floating card */}
              <div className="absolute -bottom-4 -left-4 lg:bottom-8 lg:-left-8 bg-card p-4 rounded-2xl shadow-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                    <span className="text-lg">🌍</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Klar til eventyr?</p>
                    <p className="text-xs text-muted-foreground">Start i dag</p>
                  </div>
                </div>
              </div>

              {/* Secondary floating card */}
              <div className="absolute -top-2 -right-2 lg:top-8 lg:-right-4 bg-card p-3 rounded-xl shadow-lg border border-border">
                <div className="flex items-center gap-2">
                  <span className="text-lg">✈️</span>
                  <span className="text-sm font-medium text-foreground">2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
